import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BEREICH_NAMEN, type Bereich } from '@beholder/shared';
import type { KiGespraech, KiProtokollEintrag, KiStatus, KiVorschlag } from '@beholder/shared/module/ki';
import { api, modulPfad } from '../../core/api';
import type { ModulSeiteProps } from '../../core/modul';
import { Karte, Knopf, Laden, Meldung, datumZeit, useAktion, useDaten } from '../../core/ui';
import { ANBIETER_NAME, chatOeffnen } from './api';
import { VorschlagKarte } from './Chat';

const PROJEKT_ANBIETER: Record<KiStatus['projektAnbieter'], string> = {
  lokal: 'Lokale KI, Claude als Ersatz',
  claude: 'Nur Claude',
  aus: 'Ausgeschaltet',
};

interface DateiZustand {
  id: string;
  name: string;
  bereich: Bereich;
  zustand: 'gelesen' | 'wartet' | 'uebersprungen' | 'fehler';
  hinweis: string | null;
}

export function KiSeite({ projekt }: ModulSeiteProps) {
  const pfad = (r: string) => modulPfad(projekt.id, 'ki', r);
  const status = useDaten(() => api.get<KiStatus>(pfad('status')), [projekt.id]);
  const vorschlaege = useDaten(() => api.get<KiVorschlag[]>(pfad('vorschlaege?status=offen')), [projekt.id]);
  const gespraeche = useDaten(() => api.get<KiGespraech[]>(pfad('gespraeche')), [projekt.id]);
  const protokoll = useDaten(() => api.get<KiProtokollEintrag[]>(pfad('protokoll?anzahl=100')), [projekt.id]);
  const dateien = useDaten(() => api.get<DateiZustand[]>(pfad('dateien')), [projekt.id]);
  const aktion = useAktion();

  async function vorschlagAktion(v: KiVorschlag, was: 'ausfuehren' | 'verwerfen') {
    await aktion.ausfuehren(() => api.post<KiVorschlag>(pfad(`vorschlaege/${v.id}/${was}`)));
    await vorschlaege.neuLaden();
  }

  return (
    <div className="seite ki-seite">
      <div className="seiten-kopf">
        <h2>KI-Hilfe</h2>
        {projekt.kiAnbieter !== 'aus' && (
          <Knopf art="haupt" onClick={() => chatOeffnen(null)}>
            Neue Frage stellen
          </Knopf>
        )}
      </div>
      <Meldung>{aktion.fehler}</Meldung>

      <Karte
        titel="Welche KI antwortet?"
        aktion={
          <Knopf klein onClick={() => void status.neuLaden()} disabled={status.laedt}>
            Neu prüfen
          </Knopf>
        }
      >
        {status.fehler && <Meldung>{status.fehler}</Meldung>}
        {!status.daten && !status.fehler && <Laden text="Prüft …" />}
        {status.daten && <StatusAnzeige s={status.daten} verwalten={projekt.verwalten} projektId={projekt.id} />}
      </Karte>

      <div className="ki-zwei">
        <Karte titel="Meine offenen Vorschläge">
          {vorschlaege.daten?.length === 0 && <p className="leise">Keine offenen Vorschläge.</p>}
          <div className="stapel">
            {vorschlaege.daten?.map((v) => (
              <VorschlagKarte key={v.id} v={v} beiAktion={vorschlagAktion} />
            ))}
          </div>
        </Karte>

        <Karte titel="Meine Gespräche">
          {gespraeche.daten?.length === 0 && <p className="leise">Noch keine Gespräche in diesem Projekt.</p>}
          <ul className="ki-gespraeche">
            {gespraeche.daten?.map((g) => (
              <li key={g.id}>
                <button type="button" className="ki-link" onClick={() => chatOeffnen(g.id)}>
                  {g.titel}
                </button>
                <span className="leise klein">
                  {g.anzahl} Nachrichten · {datumZeit(g.geaendertAm)}
                </span>
                <Knopf
                  art="leise"
                  klein
                  aria-label="Gespräch löschen"
                  onClick={async () => {
                    if (!confirm(`Gespräch „${g.titel}“ löschen?`)) return;
                    await aktion.ausfuehren(() => api.delete(pfad(`gespraeche/${g.id}`)));
                    await gespraeche.neuLaden();
                  }}
                >
                  ✕
                </Knopf>
              </li>
            ))}
          </ul>
        </Karte>
      </div>

      <Karte titel="Dateien, die die KI lesen kann">
        <DateiListe liste={dateien.daten} projektId={projekt.id} neuLaden={() => void dateien.neuLaden()} />
      </Karte>

      <Karte
        titel="Protokoll der Anfragen"
        aktion={
          <Knopf klein onClick={() => void protokoll.neuLaden()}>
            Aktualisieren
          </Knopf>
        }
      >
        {protokoll.daten?.length === 0 && <p className="leise">Noch keine Anfragen.</p>}
        {!!protokoll.daten?.length && (
          <div className="tabelle-rahmen">
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Zweck</th>
                  <th>KI</th>
                  <th>Dauer</th>
                  <th>Ergebnis</th>
                  <th>Wer</th>
                </tr>
              </thead>
              <tbody>
                {protokoll.daten.map((e) => (
                  <tr key={e.id}>
                    <td>{datumZeit(e.erstelltAm)}</td>
                    <td>{ZWECK[e.zweck] ?? e.zweck}</td>
                    <td title={e.modell ?? ''}>{e.anbieter ? ANBIETER_NAME[e.anbieter] : '–'}</td>
                    <td>{dauer(e.dauerMs)}</td>
                    <td className="ki-umbruch">{e.ok ? <span className="ki-gut">ok</span> : <span className="ki-schlecht">{e.fehler ?? 'Fehler'}</span>}</td>
                    <td>{e.benutzerName ?? 'automatisch'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
    </div>
  );
}

const ZWECK: Record<string, string> = { chat: 'Chat', dienst: 'Auswertung' };

function dauer(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1).replace('.', ',')} s`;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)} min ${s % 60} s`;
}

function Punkt({ gut, children, neutral }: { gut: boolean; neutral?: boolean; children: ReactNode }) {
  return (
    <li className={neutral ? 'ki-neutral' : gut ? 'ki-gut' : 'ki-schlecht'}>
      <span className="ki-punkt" aria-hidden />
      <span>{children}</span>
    </li>
  );
}

function StatusAnzeige({ s, verwalten, projektId }: { s: KiStatus; verwalten: boolean; projektId: string }) {
  return (
    <div className="stapel">
      <div className="reihe">
        <span>
          Einstellung im Projekt: <strong>{PROJEKT_ANBIETER[s.projektAnbieter]}</strong>
        </span>
        {verwalten && (
          <Link to={`/p/${projektId}/einstellungen`} className="klein">
            ändern
          </Link>
        )}
      </div>
      {s.projektAnbieter !== 'aus' && (
        <div className={`meldung ${s.jetzt ? 'meldung-ok' : 'meldung-fehler'}`}>
          {s.jetzt ? (
            <>
              Jetzt antwortet: <strong>{ANBIETER_NAME[s.jetzt]}</strong>
            </>
          ) : (
            'Gerade ist keine KI verfügbar.'
          )}
          {s.hinweis && <div className="klein">{s.hinweis}</div>}
        </div>
      )}
      <div className="ki-zwei">
        <div>
          <h4 className="ki-unter">Lokale KI (Ollama)</h4>
          <ul className="ki-punkte">
            <Punkt gut={s.lokal.erreichbar}>{s.lokal.erreichbar ? 'Rechner erreichbar' : s.lokal.fehler ?? 'Nicht erreichbar'}</Punkt>
            {s.lokal.erreichbar && (
              <Punkt gut={s.lokal.vorhanden}>
                Modell <code>{s.lokal.modell}</code> {s.lokal.vorhanden ? 'vorhanden' : 'fehlt auf dem Rechner'}
              </Punkt>
            )}
            {s.lokal.erreichbar && s.lokal.vorhanden && (
              <Punkt gut neutral={!s.lokal.geladen}>
                {s.lokal.geladen ? 'Gerade geladen (antwortet sofort)' : 'Nicht geladen – startet bei Bedarf'}
              </Punkt>
            )}
            <Punkt gut neutral>
              Bleibt nach einer Anfrage {haltezeitText(s.lokal.haltezeit)} geladen
            </Punkt>
            {s.lokal.ruhezeit && (
              <Punkt gut={!s.lokal.inRuhezeit} neutral={!s.lokal.inRuhezeit}>
                Gesperrt von {s.lokal.ruhezeit} Uhr (Einstellung KI_RUHEZEIT){s.lokal.inRuhezeit ? ' – gerade jetzt' : ''}
              </Punkt>
            )}
            {s.lokal.erreichbar && s.lokal.bildModell && (
              <Punkt gut={s.lokal.bildVorhanden}>
                Bild-Modell <code>{s.lokal.bildModell}</code> für Fotos und Scans{' '}
                {s.lokal.bildVorhanden ? (s.lokal.bildGeladen ? '– gerade geladen' : 'vorhanden') : 'fehlt auf dem Rechner'}
              </Punkt>
            )}
            {!s.lokal.bildModell && (
              <Punkt gut neutral>
                Texterkennung für Fotos und Scans ausgeschaltet (Einstellung OLLAMA_BILDMODELL)
              </Punkt>
            )}
            {s.lokal.andereGeladen.length > 0 && (
              <Punkt gut neutral>
                Außerdem geladen: {s.lokal.andereGeladen.join(', ')}
              </Punkt>
            )}
          </ul>
        </div>
        <div>
          <h4 className="ki-unter">Claude (Ersatz)</h4>
          <ul className="ki-punkte">
            <Punkt gut={s.claude.eingerichtet}>
              {s.claude.eingerichtet ? (
                <>
                  Eingerichtet, Modell <code>{s.claude.modell}</code>
                </>
              ) : (
                'Noch nicht eingerichtet (Schlüssel fehlt)'
              )}
            </Punkt>
          </ul>
        </div>
        <div>
          <h4 className="ki-unter">Internet-Suche</h4>
          <ul className="ki-punkte">
            <Punkt gut={s.suche.erreichbar} neutral={!s.suche.url}>
              {s.suche.url ? (s.suche.erreichbar ? 'Lokale Suche (SearXNG) erreichbar' : `Lokale Suche (SearXNG): ${s.suche.fehler ?? 'nicht erreichbar'}`) : 'Lokale Suche (SearXNG) nicht eingerichtet'}
            </Punkt>
            <Punkt gut neutral>
              {s.suche.url
                ? `Zuerst lokal (SearXNG + lokale KI)${s.claude.eingerichtet ? ', Claude-Websuche als Ersatz' : ''}`
                : s.claude.eingerichtet
                  ? 'Sucht über die Claude-Websuche'
                  : 'Ohne SearXNG und Claude gibt es keine Internet-Suche'}
            </Punkt>
            <Punkt gut neutral>
              Gefundene Seiten werden nie von Beholder geöffnet – Adressen nur als Text
            </Punkt>
          </ul>
        </div>
      </div>
    </div>
  );
}

function haltezeitText(h: string): string {
  const m = /^(\d+)\s*([smh])$/.exec(h.trim());
  if (!m) return h;
  const n = Number(m[1]);
  return `${n} ${m[2] === 's' ? 'Sekunden' : m[2] === 'm' ? (n === 1 ? 'Minute' : 'Minuten') : n === 1 ? 'Stunde' : 'Stunden'}`;
}

const DATEI_ZUSTAND: Record<DateiZustand['zustand'], string> = {
  gelesen: 'gelesen',
  wartet: 'wartet',
  uebersprungen: 'nicht lesbar',
  fehler: 'Fehler',
};

function DateiListe({ liste, projektId, neuLaden }: { liste: DateiZustand[] | null; projektId: string; neuLaden(): void }) {
  const [alle, setAlle] = useState(false);
  const aktion = useAktion();
  if (!liste) return <Laden />;
  if (!liste.length) return <p className="leise">Im Projekt liegen noch keine Dateien.</p>;
  const zahl = (z: DateiZustand['zustand']) => liste.filter((d) => d.zustand === z).length;
  const offen = liste.filter((d) => d.zustand !== 'gelesen');
  const zeigen = alle ? liste : offen;
  return (
    <div className="stapel">
      <p className="leise klein" style={{ margin: 0 }}>
        {zahl('gelesen')} gelesen · {zahl('wartet')} warten · {zahl('uebersprungen') + zahl('fehler')} nicht lesbar. Neue Dateien werden
        nach und nach von selbst gelesen; Bilder und gescannte PDFs kann die KI noch nicht lesen.
      </p>
      <Meldung>{aktion.fehler}</Meldung>
      {zeigen.length > 0 && (
        <ul className="ki-dateien">
          {zeigen.map((d) => (
            <li key={d.id}>
              <span className="ki-umbruch">{d.name}</span>
              <span className="marke">{BEREICH_NAMEN[d.bereich]}</span>
              <span className={`marke ${d.zustand === 'gelesen' ? '' : 'marke-warn'}`} title={d.hinweis ?? ''}>
                {DATEI_ZUSTAND[d.zustand]}
              </span>
              {d.zustand !== 'gelesen' && (
                <Knopf
                  klein
                  disabled={aktion.laeuft}
                  onClick={async () => {
                    await aktion.ausfuehren(() => api.post(modulPfad(projektId, 'ki', `dateien/${d.id}/auslesen`)));
                    neuLaden();
                  }}
                >
                  Jetzt lesen
                </Knopf>
              )}
              {d.hinweis && <span className="leise klein ki-datei-hinweis">{d.hinweis}</span>}
            </li>
          ))}
        </ul>
      )}
      <div>
        <Knopf art="leise" klein onClick={() => setAlle((a) => !a)}>
          {alle ? 'Nur ungelesene zeigen' : `Alle ${liste.length} Dateien zeigen`}
        </Knopf>
      </div>
    </div>
  );
}
